import {
  Image as ImageIcon,
  Loader2,
  RefreshCw,
  Save,
  Upload,
} from "lucide-react";
import { Card, fmtDateTime } from "../_shared";
import RichTextEditor from "@/components/RichTextEditor";
import { TONE_SWATCH } from "./constants";

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-xs font-medium text-slate-700 mb-1.5">
        {label}
      </span>
      {children}
    </label>
  );
}

function ImagePicker({ draft, setField, uploading, onUpload, fileInputRef }) {
  return (
    <Field label="Hero image (optional)">
      <div className="flex items-center gap-3">
        <div
          className="h-20 w-32 rounded-lg border border-dashed border-slate-200 bg-slate-50 flex items-center justify-center overflow-hidden"
          data-testid="admin-cover-image-preview"
        >
          {draft.image_url ? (
            <img
              src={draft.image_url}
              alt="Cover hero"
              className="h-full w-full object-cover"
            />
          ) : (
            <ImageIcon className="h-5 w-5 text-slate-400" />
          )}
        </div>
        <div className="flex-1 space-y-2">
          <input
            type="file"
            accept="image/png,image/jpeg,image/webp,image/gif,image/svg+xml"
            ref={fileInputRef}
            onChange={(e) => onUpload(e.target.files?.[0])}
            className="hidden"
            data-testid="admin-cover-image-input"
          />
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
            data-testid="admin-cover-image-upload"
          >
            {uploading ? (
              <Loader2 className="h-3 w-3 animate-spin" />
            ) : (
              <Upload className="h-3 w-3" />
            )}
            {draft.image_url ? "Replace image" : "Upload image"}
          </button>
          {draft.image_url && (
            <button
              type="button"
              onClick={() => setField("image_url", "")}
              className="ml-2 text-xs text-slate-500 hover:text-slate-700"
              data-testid="admin-cover-image-remove"
            >
              Remove
            </button>
          )}
          <p className="text-[11px] text-slate-400">
            PNG, JPG, WebP, GIF or SVG · max 5MB
          </p>
        </div>
      </div>
    </Field>
  );
}

function TonePicker({ value, options, onChange }) {
  return (
    <Field label="Accent tone">
      <div className="flex flex-wrap gap-2" data-testid="admin-cover-tone-group">
        {options.map((tone) => {
          const isActive = (value || "slate") === tone;
          return (
            <button
              key={tone}
              type="button"
              onClick={() => onChange(tone)}
              className={`inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs capitalize ${
                isActive
                  ? "border-slate-900 bg-slate-900 text-white"
                  : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
              }`}
              data-testid={`admin-cover-tone-${tone}`}
            >
              <span
                className={`inline-block h-3 w-3 rounded-full ${
                  TONE_SWATCH[tone] || "bg-slate-200"
                }`}
              />
              {tone}
            </button>
          );
        })}
      </div>
    </Field>
  );
}

function EditorFooter({ saved, onReset, onSave, isDirty, savingActive, resettingActive }) {
  return (
    <div className="flex items-center justify-between pt-2 border-t border-slate-100">
      <div className="text-xs text-slate-500" data-testid="admin-cover-meta">
        {saved.updated_at ? (
          <>
            Last saved {fmtDateTime(saved.updated_at)} by{" "}
            <span className="text-slate-700">
              {saved.updated_by || "system"}
            </span>
          </>
        ) : (
          "Using built-in defaults (nothing saved yet)."
        )}
      </div>
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={onReset}
          disabled={resettingActive}
          className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-50 disabled:opacity-50"
          data-testid="admin-cover-reset-button"
        >
          {resettingActive ? (
            <Loader2 className="h-3 w-3 animate-spin" />
          ) : (
            <RefreshCw className="h-3 w-3" />
          )}
          Reset to default
        </button>
        <button
          type="button"
          onClick={onSave}
          disabled={!isDirty || savingActive}
          className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-emerald-700 disabled:opacity-50"
          data-testid="admin-cover-save-button"
        >
          {savingActive ? (
            <Loader2 className="h-3 w-3 animate-spin" />
          ) : (
            <Save className="h-3 w-3" />
          )}
          Save changes
        </button>
      </div>
    </div>
  );
}

export default function CoverEditor({
  draft,
  saved,
  setField,
  onSave,
  onReset,
  onUpload,
  uploading,
  fileInputRef,
  validTones,
  savingActive,
  resettingActive,
  isDirty,
}) {
  const tones = validTones.length ? validTones : Object.keys(TONE_SWATCH);
  return (
    <Card className="p-5 xl:col-span-3 space-y-4" testid="admin-cover-editor">
      <Field label="Eyebrow (small label above the title)">
        <input
          type="text"
          value={draft.eyebrow || ""}
          onChange={(e) => setField("eyebrow", e.target.value)}
          placeholder="Coming soon"
          className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm focus:outline-none focus:border-sky-500 focus:ring-2 focus:ring-sky-100"
          data-testid="admin-cover-eyebrow"
        />
      </Field>
      <Field label="Title">
        <input
          type="text"
          value={draft.title || ""}
          onChange={(e) => setField("title", e.target.value)}
          placeholder="Something quietly useful is on the way."
          className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-base focus:outline-none focus:border-sky-500 focus:ring-2 focus:ring-sky-100"
          data-testid="admin-cover-title"
        />
      </Field>
      <Field label="Body">
        <RichTextEditor
          value={draft.body_html || ""}
          onChange={(html) => setField("body_html", html)}
          placeholder="Write the main message visitors will read…"
          testid="admin-cover-body"
        />
      </Field>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Field label="Button label">
          <input
            type="text"
            value={draft.button_label || ""}
            onChange={(e) => setField("button_label", e.target.value)}
            placeholder="Contact us"
            className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm focus:outline-none focus:border-sky-500 focus:ring-2 focus:ring-sky-100"
            data-testid="admin-cover-button-label"
          />
        </Field>
        <Field label="Button URL">
          <input
            type="text"
            value={draft.button_url || ""}
            onChange={(e) => setField("button_url", e.target.value)}
            placeholder="/contact"
            className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-mono focus:outline-none focus:border-sky-500 focus:ring-2 focus:ring-sky-100"
            data-testid="admin-cover-button-url"
          />
        </Field>
      </div>

      <ImagePicker
        draft={draft}
        setField={setField}
        uploading={uploading}
        onUpload={onUpload}
        fileInputRef={fileInputRef}
      />

      <TonePicker
        value={draft.tone}
        options={tones}
        onChange={(t) => setField("tone", t)}
      />

      <EditorFooter
        saved={saved}
        onReset={onReset}
        onSave={onSave}
        isDirty={isDirty}
        savingActive={savingActive}
        resettingActive={resettingActive}
      />
    </Card>
  );
}
